import styles from "./OrderCard.module.css"
import React, { useState, useEffect } from 'react';
import { getPrimaryProductImage } from "../../../services/productImageService";
import { ChevronRight, ChevronDown, ShoppingCart } from 'lucide-react';
import SecondaryButton from "../../../components/Button/SecondaryButton/SecondaryButton";
import { useWindowSize } from "../../../hooks/useWindowSize";
import { useProducts } from "../../../context/ProductContext";
import { useStorageItems } from "../../../context/StorageItemsContext";
import { useTranslation } from "react-i18next";
import { czk_eur_rate } from "../../../constants/prices";
import { usePreferences } from "../../../context/PreferencesContext";
import { timeToMinutes } from "../../../utils/calendar";
import { usePastOrderContext } from "../../../context/PastOrderContext";
import { formatCurrencyFromCzk } from "../../../utils/prices";


// Sample image
import { PastOrder } from "../../../types/order.types";
import { groupCartData } from "../../../utils/cart";
/**
* Props for the OrderCard component.
*/ 
interface OrderCardProps {
    order: PastOrder
}
/**
* A card component representing a single order.
*
* @param props OrderCard props
* @param {number|string} props.id - Order ID
* @param {string} props.orderDate - Order date
* @param {string} props.orderNumber - Order number
* @param {string|number} props.orderPrice - Order price
* @param {() => void} props.onClick - Callback fired when the card is clicked
* @returns TSX.Element
*/
const OrderCard = ({ order }: OrderCardProps) => {
    const screenWidth = useWindowSize(); // Screen width
    const { t, i18n } = useTranslation(); // Translation
    const [cardOpen, setCardOpen] = useState<boolean>(false) // Card open flag
    const { selectedCurrency } = usePreferences(); // Curent selected currency

    const { openPastOrder, openedPastOrderCard, closePastOrder, pastOrderOpen, setPastOrderOpen} = usePastOrderContext();

    const { allProducts, packages, loading, reloadProducts } = useProducts() // Products
    const { items, reloadItems, loading: loadingItems } = useStorageItems(); // Storage items
        // Reload products if they are loaded but dont appear
        useEffect(() => {
            if (!loading && !allProducts) {
                reloadProducts();
            }
            if (!loadingItems && !items) {
                reloadItems();
            }
        },[loading, loadingItems])

    const groupedCart = groupCartData(order ?? null); // Grouped cart by date and time
    const noDphCzk = order?.totals?.total_price_without_tax ?? 0;
    return(
        <>
            <div className={styles.Order} key={order.id}>
                <div className={styles.OrderHeader}>
                    <div className={styles.OrderDate}>
                        <button className={styles.OrderButton} onClick={()=>setCardOpen(!cardOpen)}>
                            {cardOpen === true ? <ChevronDown/> : <ChevronRight/>}
                        </button>
                        <p>{t("account.orders.order_num")} {order.order?.code ?? ""}</p>
                        
                    </div>
                    <div className={styles.OrderNumber}>
                    </div>
                    <div className={styles.OrderPrice}>
                        <p>{formatCurrencyFromCzk(noDphCzk, czk_eur_rate, selectedCurrency)}</p>
                    </div>
                    
                </div>
                {cardOpen && (
                    <>
                        <div className={styles.OrderContent}>
                            {Object.entries(groupedCart)
                            .sort(([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime()) // Sort the dates ( substracting them gives - / + which sort uses for sorting)
                            .map(([date, times]) =>  // Loop through the dates
                                Object.entries(times)
                                .sort(([timeA], [timeB]) => {
                                    // Turn the times into minutes and sort
                                    const minutesA = timeToMinutes(timeA.split("-")[0]);
                                    const minutesB = timeToMinutes(timeB.split("-")[0]);
                                    return minutesA - minutesB;
                                })
                                .map(([time, products], i) => {// Loop throught the times
                                    return (
                                    <React.Fragment key={`${date}-${time}`}>
                                    <div className={styles.OrderDateHeader}>
                                        <p className={styles.Date}>{date}</p>
                                        <p className={styles.CartTime}>{time}</p>
                                    </div>

                                    <div className={styles.OrderProducts}>
                                        {products.map((prod, k) => {
                                            if (prod.category === "sales") {
                                                
                                                const product = allProducts.find(p => p.id === Number(prod.id));
                                                if (!product) return null;
                                                return (
                                                    <div className={styles.Item} key={k}>
                                                        <div className={styles.ItemImage} style={{ backgroundImage: `url(${getPrimaryProductImage(product)})` }}></div>
                                                        <p className={styles.ItemTitle}>{i18n.language === "en" ? product.name_en || "N/A" : product.name}</p>
                                                        <p className={styles.ItemPrice}>
                                                            {formatCurrencyFromCzk(
                                                                product.pricing.price_without_tax ?? 0,
                                                                czk_eur_rate,
                                                                selectedCurrency
                                                            )}
                                                        </p>
                                                        {/*
                                                        prod.price_without_tax - price with the amount in mind
                                                        */}
                                                    </div>
                                                );

                                            }else if (prod.category === "package") {
                                                
                                                const product = packages.find(p => p.id === Number(prod.id));
                                                if (!product) return null;
                                                return (
                                                    <div className={styles.Item} key={k}>
                                                        <div className={styles.ItemImage} style={{ backgroundImage: `url(${getPrimaryProductImage(product)})` }}></div>
                                                        <p className={styles.ItemTitle}>{i18n.language === "en" ? product.name_en || "N/A" : product.name}</p>
                                                        <p className={styles.ItemPrice}>
                                                            {formatCurrencyFromCzk(
                                                                product.pricing?.price_without_tax ?? 0,
                                                                czk_eur_rate,
                                                                selectedCurrency
                                                            )}
                                                        </p>
                                                    </div>
                                                );

                                            }else if(prod.category === "storage"){

                                                const product = items.find(p => p.id === Number(prod.id));
                                                if (!product) return null;
                                                return (
                                                    <div className={styles.Item} key={k}>
                                                        <div className={styles.ItemImage} style={{ backgroundImage: `url(${getPrimaryProductImage(product)})` }}></div>
                                                        <p className={styles.ItemTitle}>{i18n.language === "en" ? product.name_en || "N/A" : product.name}</p>
                                                        <p className={styles.ItemPrice}>
                                                            {formatCurrencyFromCzk(
                                                                product.pricing?.price_without_tax ?? 0,
                                                                czk_eur_rate,
                                                                selectedCurrency
                                                            )}
                                                        </p>
                                                    </div>
                                                );

                                            }
                                        })}
                                    </div>

                                    {screenWidth > 700 ? (
                                        <SecondaryButton
                                        text={t("buttons.order")}
                                        className={styles.OrderCartButton}
                                        onClick={()=>openPastOrder({
                                            products: products,
                                            order: order,
                                            date: date,
                                            time: time,
                                            index: i
                                        })}
                                        Icon={ShoppingCart}
                                        colorVariant="secondary"
                                        disabled={!cardOpen}
                                        />
                                    ) : (
                                        <SecondaryButton
                                        text=""
                                        className={styles.OrderCartButton}
                                        onClick={()=>openPastOrder({
                                            products: products,
                                            order: order,
                                            date: date,
                                            time: time,
                                            index: i
                                        })}
                                        Icon={ShoppingCart}
                                        colorVariant="secondary"
                                        disabled={!cardOpen}
                                        />
                                    )}

                                    </React.Fragment>
                                    );
                                })

                                    
                                )}
                        </div>
                    </>
                )}
            </div>
        </>
    )
}

export default OrderCard;
